import { useState } from 'react';
import { Flag, X, CheckCircle } from 'lucide-react';
import { reportContent } from '../stores/sharedNotes$';
import { cn } from '../lib/utils';
import Modal from './Modal';

const REPORT_REASONS = [
  { value: 'hate_violence', label: 'Hate speech or violence' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'spam', label: 'Spam or misleading' },
  { value: 'explicit', label: 'Explicit or sexual content' },
  { value: 'illegal', label: 'Illegal content' },
  { value: 'other', label: 'Other' },
];

/**
 * ReportContentModal - UGC compliance report dialog
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} props.contentType - 'shared_note' | 'shared_tag_note'
 * @param {string} props.contentId - share_token or note_id
 */
export default function ReportContentModal({ isOpen, onClose, contentType, contentId }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) {
      setError('Please select a reason');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await reportContent({ contentType, contentId, reason, details });
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit report:', err);
      setError('Failed to submit report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setDetails('');
    setError('');
    setSubmitted(false);
    onClose?.();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} onOverlayClick={handleClose}>
      <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-lg animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={cn(
              "h-10 w-10 rounded-full flex items-center justify-center",
              submitted ? "bg-green-500/10 text-green-600" : "bg-destructive/10 text-destructive"
            )}>
              {submitted ? <CheckCircle size={20} /> : <Flag size={20} />}
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                {submitted ? 'Report Submitted' : 'Report Content'}
              </h2>
              <p className="text-xs text-muted-foreground">
                {submitted ? 'Thank you for helping keep PromptNote safe' : 'Help us keep PromptNote safe'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {submitted ? (
          <div className="p-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              We'll review this report and take appropriate action. If this content violates our{' '}
              <a href="/content-policy.html" target="_blank" rel="noopener noreferrer" className="underline text-foreground">
                Content Policy
              </a>, it will be removed.
            </p>
            <button
              onClick={handleClose}
              className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md text-sm font-medium transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Reason selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Why are you reporting this?</label>
              <div className="space-y-1.5">
                {REPORT_REASONS.map((r) => (
                  <label
                    key={r.value}
                    className={cn(
                      "flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors",
                      reason === r.value
                        ? "border-primary bg-primary/5"
                        : "border-transparent hover:bg-muted/50"
                    )}
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      className="accent-primary"
                    />
                    <span className="text-sm">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Optional details */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Additional details <span className="text-muted-foreground font-normal">(optional)</span></label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Describe the issue..."
                rows={3}
                maxLength={1000}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring/20 focus:border-primary resize-none"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-xs text-destructive">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 h-10 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-md text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !reason}
                className="flex-1 h-10 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>

            <p className="text-[10px] text-muted-foreground text-center">
              Reports are reviewed per our{' '}
              <a href="/content-policy.html" target="_blank" rel="noopener noreferrer" className="underline">
                Content Policy
              </a>
            </p>
          </form>
        )}
      </div>
    </Modal>
  );
}
